import { useState, useCallback } from 'react';
import pLimit from 'p-limit';

import {
	useAbortFileUploadMutation,
	useCompleateMultipleChunkUploadMutation,
	useInitiateMultipartFileUploadMutation,
	useInitiateSingleFileUploadMutation,
	useUploadMultipleChunkMutation,
	useUploadSingleChunkMutation
} from '@/api/S3UploadApis';
import { toastError, toastSuccess } from '@/components/Toast';

export const INITIAL_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB for first  chunks
export const LATER_CHUNK_SIZE = 40 * 1024 * 1024; // 40MB for later chunks
export const SWITCH_AFTER_CHUNKS = 1;

const useMultipartFileUpload = (options = {}) => {
	const {
		fileSizeThreshold = 100 * 1024 * 1024, // 100MB default
		maxRetries = 2,
		concurrentUploads = 3,
		onProgress,
		onSuccess,
		onError,
		showToasts = true
	} = options;

	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadTime, setUploadTime] = useState(0);

	const [initiateMultipartFileUpload] = useInitiateMultipartFileUploadMutation();
	const [initiateSingleFileUpload] = useInitiateSingleFileUploadMutation();
	const [uploadSingleChunk] = useUploadSingleChunkMutation();
	const [uploadMultipleChunk] = useUploadMultipleChunkMutation();
	const [compleateMultipleChunkUpload] = useCompleateMultipleChunkUploadMutation();
	const [abortFileUpload] = useAbortFileUploadMutation();

	const isLargeFile = useCallback(
		(fileSize) => {
			return fileSize >= fileSizeThreshold;
		},
		[fileSizeThreshold]
	);

	const resetState = useCallback(() => {
		setUploadProgress(0);
		setUploadTime(0);
	}, []);

	const createChunks = useCallback(
		(file) => {
			if (isLargeFile(file?.size)) {
				const chunks = [];
				let start = 0;
				while (start < file.size) {
					const chunkNumber = chunks.length + 1;
					const currentChunkSize =
						chunkNumber <= SWITCH_AFTER_CHUNKS ? INITIAL_CHUNK_SIZE : LATER_CHUNK_SIZE;
					const end = Math.min(start + currentChunkSize, file.size);

					chunks.push({
						blob: file.slice(start, end),
						start,
						end,
						attempts: 0,
						partNumber: chunkNumber
					});
					start = end;
				}
				return chunks;
			} else {
				return [
					{
						blob: file,
						attempts: 0,
						partNumber: 1
					}
				];
			}
		},
		[isLargeFile]
	);

	const initiateUploadBasedOnSize = useCallback(
		async (file, documentType = 'ARTWORK_ILLUSTRATOR_FILE') => {
			const chunks = createChunks(file);
			let initResponse;

			const requestBodyForMultipartFile = {
				name: file?.name,
				docMimeType: file.type || 'application/octet-stream',
				partCount: chunks.length,
				documentType
			};

			const requestBodyForSingleFile = {
				name: file?.name,
				docMimeType: file.type || 'application/octet-stream',
				documentType
			};

			try {
				if (isLargeFile(file?.size)) {
					initResponse = await initiateMultipartFileUpload({ body: requestBodyForMultipartFile });
				} else {
					initResponse = await initiateSingleFileUpload({ body: requestBodyForSingleFile });
				}
				return { initResponse, chunks };
			} catch (error) {
				const errorMessage = error?.data?.message || 'Something went wrong!';
				if (showToasts) toastError(errorMessage);
				if (onError) onError(error);
				throw error;
			}
		},
		[
			createChunks,
			isLargeFile,
			initiateMultipartFileUpload,
			initiateSingleFileUpload,
			showToasts,
			onError
		]
	);

	const uploadChunkWithRetry = useCallback(
		async (chunk, url, isLargeFileFlag, file, onChunkProgress) => {
			while (chunk.attempts < maxRetries) {
				try {
					let res;

					if (isLargeFileFlag) {
						res = await uploadMultipleChunk({
							url: url,
							body: chunk.blob,
							fileMimeType: file.type || 'application/octet-stream'
						});
					} else {
						// For single file uploads
						if (onChunkProgress) {
							const progressInterval = setInterval(() => {}, 100);
							clearInterval(progressInterval);
						}

						res = await uploadSingleChunk({
							url: url,
							body: chunk.blob,
							fileMimeType: file.type || 'application/octet-stream'
						});
					}

					if (res?.error) {
						if (showToasts) toastError('Failed to upload chunk');
						throw new Error('Failed to upload chunk');
					}

					if (onChunkProgress) {
						onChunkProgress();
					}

					const etag = res.data?.eTag || '';

					return {
						partNumber: chunk.partNumber,
						eTag: etag.replaceAll('"', '')
					};
				} catch (err) {
					chunk.attempts++;
					if (chunk.attempts === maxRetries) throw err;

					await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, chunk.attempts)));
				}
			}
		},
		[maxRetries, uploadMultipleChunk, uploadSingleChunk, showToasts]
	);

	const uploadChunks = useCallback(
		async (initData, chunks, file) => {
			const { uploadId, objectKey, urlList, documentId } = initData;

			if (!urlList || urlList.length === 0) {
				const errorMessage = 'No upload URLs received from server';
				if (showToasts) toastError(errorMessage);
				throw new Error(errorMessage);
			}

			const numParts = chunks.length;
			const etags = new Array(numParts);
			let completedParts = 0;
			const isLarge = isLargeFile(file.size);

			const updateProgress = () => {
				const progress = Math.min((completedParts / numParts) * 100, 100);
				setUploadProgress(progress);
				if (onProgress) onProgress(progress);
			};

			const onChunkComplete = () => {
				completedParts++;
				updateProgress();
			};

			// Initialize progress
			setUploadProgress(0);
			if (onProgress) onProgress(0);

			if (isLarge) {
				// Upload first chunk
				const firstChunkResult = await uploadChunkWithRetry(
					chunks[0],
					urlList[0],
					true,
					file,
					onChunkComplete
				);
				etags[0] = firstChunkResult;

				// Upload remaining chunks in parallel
				const limit = pLimit(concurrentUploads);
				await Promise.all(
					chunks.slice(1).map((chunk, idx) =>
						limit(async () => {
							const result = await uploadChunkWithRetry(
								chunk,
								urlList[idx + 1],
								true,
								file,
								onChunkComplete
							);
							etags[idx + 1] = result;
							return result;
						})
					)
				);
			} else {
				// For single file uploads
				let progressSimulation;
				let currentProgress = 0;

				const simulateProgress = () => {
					if (currentProgress < 90) {
						currentProgress += Math.random() * 15; // Random increment between 0-15%
						currentProgress = Math.min(currentProgress, 90); // Cap at 90%
						setUploadProgress(currentProgress);
						if (onProgress) onProgress(currentProgress);
					}
				};

				progressSimulation = setInterval(simulateProgress, 200);

				try {
					const singleChunkResult = await uploadChunkWithRetry(
						chunks[0],
						urlList[0],
						false,
						file,
						onChunkComplete
					);
					etags[0] = singleChunkResult;
				} finally {
					clearInterval(progressSimulation);
				}
			}

			setUploadProgress(100);
			if (onProgress) onProgress(100);

			const cleanUrl = urlList[0].split('?')[0];
			return { etags, uploadId, objectKey, documentId, downloadUrl: cleanUrl };
		},
		[isLargeFile, uploadChunkWithRetry, concurrentUploads, onProgress, showToasts]
	);

	const handleCompleateUploadBasedOnSize = useCallback(
		async (file, uploadData) => {
			const { etags, uploadId, objectKey, documentId } = uploadData;

			try {
				let completeResponse;

				if (isLargeFile(file.size)) {
					completeResponse = await compleateMultipleChunkUpload({
						body: {
							key: objectKey,
							uploadId,
							partList: etags.map(({ partNumber, eTag }) => ({ partNumber, eTag })),
							documentId
						}
					});
				}

				return completeResponse;
			} catch (error) {
				const errorMessage = error?.data?.message || 'Failed to complete upload';
				if (showToasts) toastError(errorMessage);
				if (onError) onError(error);
				resetState();
				throw error;
			}
		},
		[isLargeFile, compleateMultipleChunkUpload, showToasts, onError, resetState]
	);

	const handleAbortFileUpload = useCallback(
		async (uploadId, objectKey) => {
			try {
				await abortFileUpload({ uploadId, key: objectKey });
				if (showToasts) toastSuccess('Upload aborted successfully!');
			} catch (error) {
				const errorMessage = error?.data?.message || 'Failed to abort upload';
				if (showToasts) toastError(errorMessage);
				if (onError) onError(error);
			}
		},
		[abortFileUpload, showToasts, onError]
	);

	// Main upload function
	const uploadFile = useCallback(
		async (file, documentType = 'ARTWORK_ILLUSTRATOR_FILE') => {
			if (!file) {
				const errorMessage = 'No file provided';
				if (showToasts) toastError(errorMessage);
				return null;
			}

			if (file.size === 0) {
				const errorMessage = 'File is empty. Please select a valid file.';
				if (showToasts) toastError(errorMessage);
				return null;
			}

			setIsUploading(true);
			resetState();

			let uploadId, objectKey;

			try {
				const { initResponse, chunks } = await initiateUploadBasedOnSize(file, documentType);
				const initData = initResponse.data;
				uploadId = initData.uploadId;
				objectKey = initData.objectKey;

				const uploadData = await uploadChunks(initData, chunks, file);

				await handleCompleateUploadBasedOnSize(file, uploadData);

				const result = {
					documentId: uploadData.documentId,
					downloadUrl: uploadData.downloadUrl,
					file,
					documentType
				};

				if (showToasts) toastSuccess('Upload complete!');
				if (onSuccess) onSuccess(result);
				resetState();

				return result;
			} catch (error) {
				const errorMessage = error?.data?.message || 'Something went wrong!';
				if (showToasts) toastError(errorMessage);
				if (onError) onError(error);

				if (uploadId && objectKey) {
					await handleAbortFileUpload(uploadId, objectKey);
				}

				return null;
			} finally {
				setIsUploading(false);
			}
		},
		[
			initiateUploadBasedOnSize,
			uploadChunks,
			handleCompleateUploadBasedOnSize,
			handleAbortFileUpload,
			resetState,
			showToasts,
			onSuccess,
			onError
		]
	);

	// Batch upload function
	const uploadFiles = useCallback(
		async (files, documentType = 'ARTWORK_ILLUSTRATOR_FILE') => {
			const results = [];

			for (const file of files) {
				const result = await uploadFile(file, documentType);
				if (result) {
					results.push(result);
				}
			}

			return results;
		},
		[uploadFile]
	);

	return {
		// State
		uploadProgress,
		isUploading,
		uploadTime,

		// Functions
		uploadFile,
		uploadFiles,
		resetState,

		// Utilities
		isLargeFile
	};
};

export default useMultipartFileUpload;
